import {
	Button,
	Calendar,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Skeleton,
	useIsMobile,
	useUrlState,
} from "@monbits/ui";
import { getChain } from "@oxygen/concepts";
import { endOfDay, format, startOfDay, subDays } from "date-fns";
import {
	ArrowDownRight,
	ArrowUpRight,
	Calendar as CalendarIcon,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
	Area,
	AreaChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import { useAnimatedNumber } from "src/hooks/use-animated-number";
import analyticsQueries from "src/queries/analytics-queries";

// Cycled chart palette for series without an inherent identity color
const CHART_PALETTE = [
	"var(--chart-1)",
	"var(--chart-2)",
	"var(--chart-3)",
	"var(--chart-4)",
	"var(--chart-5)",
];

// Semantic status palette for order-status chart series. Matches the hex
// values in src/utils/status-config.ts so a "paid" line in the chart is
// visibly the same green as the paid icon in tables/lists. Expired uses
// red (vs status-config's gray) so the chart can distinguish 5 series.
const ORDER_STATUS_COLORS = {
	paid: "#22c55e",
	partiallyPaid: "#3b82f6",
	pending: "#eab308",
	expired: "#ef4444",
	cancelled: "#6b7280",
} as const;

const DEFAULT_COLORS = CHART_PALETTE;

const getBlockchainColor = (
	blockchain: string,
	fallbackIndex: number,
): string =>
	getChain(blockchain)?.color ||
	DEFAULT_COLORS[fallbackIndex % DEFAULT_COLORS.length];

interface TooltipPayload {
	value: number;
	name: string;
	color: string;
}

interface CustomTooltipProps {
	active?: boolean;
	payload?: TooltipPayload[];
	label?: string;
	formatter?: (value: number, name: string) => string | [string, string];
}

// Quick-select windows for the date picker. Days are inclusive of today.
const DATE_PRESETS = [
	{ id: "24h", label: "24h", days: 1 },
	{ id: "7d", label: "7d", days: 7 },
	{ id: "30d", label: "30d", days: 30 },
	{ id: "90d", label: "90d", days: 90 },
] as const;

/**
 * Animates an integer value through transitions (preset clicks). Display is
 * rounded, comma-formatted. Use for counts, not money or percentages.
 */
function AnimatedInt({ value }: { value: number }) {
	const animated = useAnimatedNumber(value);
	return <>{Math.round(animated).toLocaleString("en-US")}</>;
}

const CustomTooltip = ({
	active,
	payload,
	label,
	formatter,
}: CustomTooltipProps) => {
	if (!active || !payload || !payload.length) return null;

	return (
		<div className="rounded-lg border bg-popover p-3 text-popover-foreground shadow-md">
			<p className="mb-2 text-xs font-medium">{label}</p>
			{payload.map((entry, index: number) => {
				let displayValue: string | number = entry.value;
				let displayName = entry.name;

				if (formatter) {
					const result = formatter(entry.value, entry.name);
					if (Array.isArray(result)) {
						displayValue = result[0];
						displayName = result[1];
					} else {
						displayValue = result;
					}
				}

				return (
					<div key={index} className="flex items-center gap-2 text-xs">
						<div
							className="h-2 w-2 rounded-full"
							style={{ backgroundColor: entry.color }}
						/>
						<span className="text-muted-foreground">{displayName}:</span>
						<span className="font-medium">{displayValue}</span>
					</div>
				);
			})}
		</div>
	);
};

const DashboardPage: React.FC = () => {
	const navigate = useNavigate();
	const isMobile = useIsMobile();

	// Date range state synced to URL
	const [urlState, setUrlState] = useUrlState({
		from: {
			type: "string",
			default: format(startOfDay(subDays(new Date(), 29)), "yyyy-MM-dd"),
		},
		to: {
			type: "string",
			default: format(endOfDay(new Date()), "yyyy-MM-dd"),
		},
	});

	const dateRange = React.useMemo(
		() => ({
			from: startOfDay(new Date(urlState.from)),
			to: endOfDay(new Date(urlState.to)),
		}),
		[urlState.from, urlState.to],
	);

	const dateParams = React.useMemo(
		() => ({
			startDate: format(dateRange.from, "yyyy-MM-dd"),
			endDate: format(dateRange.to, "yyyy-MM-dd"),
		}),
		[dateRange],
	);

	// Calendar popover state
	const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
	const [calendarSelection, setCalendarSelection] = React.useState<
		{ from: Date | undefined; to?: Date | undefined } | undefined
	>(undefined);

	const handleDateRangeChange = (
		range: { from: Date | undefined; to?: Date | undefined } | undefined,
	) => {
		setCalendarSelection(range);
	};

	const handleApplyDateRange = () => {
		if (calendarSelection?.from && calendarSelection?.to) {
			setUrlState({
				from: format(startOfDay(calendarSelection.from), "yyyy-MM-dd"),
				to: format(endOfDay(calendarSelection.to), "yyyy-MM-dd"),
			});
			setIsCalendarOpen(false);
			setCalendarSelection(undefined);
		}
	};

	const activePreset = React.useMemo(() => {
		const today = endOfDay(new Date());
		if (dateRange.to.getTime() !== today.getTime()) return null;
		for (const p of DATE_PRESETS) {
			const expectedFrom = startOfDay(subDays(new Date(), p.days - 1));
			if (dateRange.from.getTime() === expectedFrom.getTime()) return p.id;
		}
		return null;
	}, [dateRange]);

	const applyPreset = (days: number) => {
		setUrlState({
			from: format(startOfDay(subDays(new Date(), days - 1)), "yyyy-MM-dd"),
			to: format(endOfDay(new Date()), "yyyy-MM-dd"),
		});
	};

	const handleCancelDateRange = () => {
		setCalendarSelection(undefined);
		setIsCalendarOpen(false);
	};

	const handleCalendarOpenChange = (open: boolean) => {
		setIsCalendarOpen(open);
		if (!open) {
			setCalendarSelection(undefined);
		}
	};

	const hasValidRange = !!(calendarSelection?.from && calendarSelection?.to);

	const overviewQuery = analyticsQueries.getOverview();
	const volumeQuery = analyticsQueries.getVolumeAnalytics(dateParams);
	const networkQuery = analyticsQueries.getNetworkBreakdown(dateParams);
	const currencyQuery = analyticsQueries.getCurrencyBreakdown(dateParams);
	const orderStatusQuery = analyticsQueries.getOrderStatusAnalytics(dateParams);

	const overview = overviewQuery.data ?? null;
	const volumeData = volumeQuery.data ?? null;
	const networkData = networkQuery.data ?? null;
	const currencyData = currencyQuery.data ?? null;
	const orderStatusData = orderStatusQuery.data ?? null;

	const isLoading =
		overviewQuery.isPending ||
		volumeQuery.isPending ||
		networkQuery.isPending ||
		currencyQuery.isPending ||
		orderStatusQuery.isPending;

	const hasError =
		overviewQuery.isError ||
		volumeQuery.isError ||
		networkQuery.isError ||
		currencyQuery.isError ||
		orderStatusQuery.isError;

	React.useEffect(() => {
		if (hasError) {
			toast.error("Failed to load dashboard", {
				description: "Please try again later",
			});
		}
	}, [hasError]);

	const formatCurrency = (amount: number | string) => {
		const value = typeof amount === "string" ? parseFloat(amount) : amount;
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(value);
	};

	const totalVolume = React.useMemo(
		() =>
			volumeData?.currencies?.reduce((sum, c) => {
				const v =
					typeof c.totalVolume === "string"
						? parseFloat(c.totalVolume)
						: c.totalVolume;
				return sum + (Number.isFinite(v) ? v : 0);
			}, 0) ?? 0,
		[volumeData],
	);

	// Hero chart series: sum per-date volumes across all currencies. For a
	// single-currency merchant this equals that currency's series. For
	// multi-currency it's the combined story.
	const totalSeries = React.useMemo(() => {
		if (!volumeData?.currencies?.length) return [];
		const dateMap = new Map<string, number>();
		for (const c of volumeData.currencies) {
			for (const point of c.series) {
				const v = typeof point.volume === "number" ? point.volume : 0;
				dateMap.set(point.date, (dateMap.get(point.date) ?? 0) + v);
			}
		}
		return Array.from(dateMap.entries())
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([date, volume]) => ({ date, volume }));
	}, [volumeData]);

	// Weighted overall trend: derive prior-period totals from per-currency
	// pct change, then compute combined movement. Returns null when there's
	// no prior-period data to compare against (avoids fabricating a story).
	const totalTrendPct = React.useMemo<number | null>(() => {
		if (!volumeData?.currencies?.length) return null;
		let currentSum = 0;
		let previousSum = 0;
		for (const c of volumeData.currencies) {
			const current =
				typeof c.totalVolume === "string"
					? parseFloat(c.totalVolume)
					: c.totalVolume;
			if (!Number.isFinite(current)) continue;
			currentSum += current;
			const previous =
				c.volumeChangePct === 0
					? current
					: current / (1 + c.volumeChangePct / 100);
			if (Number.isFinite(previous)) {
				previousSum += previous;
			}
		}
		if (previousSum === 0) return null;
		return ((currentSum - previousSum) / previousSum) * 100;
	}, [volumeData]);

	const hasAnyData =
		!!(volumeData?.currencies && volumeData.currencies.length > 0) ||
		!!(networkData?.networks && networkData.networks.length > 0) ||
		!!(currencyData?.currencies && currencyData.currencies.length > 0) ||
		!!(orderStatusData?.series && orderStatusData.series.length > 0);

	// Hover-scrubbing: when the cursor is over the hero chart, the headline
	// reflects that day's value instead of the period total.
	const [hoveredPoint, setHoveredPoint] = React.useState<{
		date: string;
		volume: number;
	} | null>(null);

	const displayedVolume = hoveredPoint?.volume ?? totalVolume;
	// During hover, snap (immediate) so the number tracks the cursor without
	// lag. On preset click / restore, animate smoothly.
	const animatedVolume = useAnimatedNumber(displayedVolume, {
		immediate: hoveredPoint !== null,
		animateOnMount: true,
	});
	const animatedTrend = useAnimatedNumber(totalTrendPct ?? 0);

	if (isLoading) {
		return (
			<div className="space-y-6 p-4 sm:p-6">
				<div className="space-y-6">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<Skeleton className="h-3 w-64" />
						<Skeleton className="h-10 w-full sm:w-[320px]" />
					</div>
					<Skeleton className="h-16 w-80 sm:h-20" />
					<div className="grid gap-6 grid-cols-1 sm:grid-cols-3">
						<Skeleton className="h-24" />
						<Skeleton className="h-24" />
						<Skeleton className="h-24" />
					</div>
					<Skeleton className="h-[280px] w-full" />
				</div>
				<div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
					<Skeleton className="h-64 lg:col-span-2" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<h1 className="sr-only">Dashboard</h1>

			<section className="space-y-6">
				{/* Top row: eyebrow on the left, date-range controls on the right */}
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between stagger-in">
					<div className="flex items-center gap-3">
						<span className="h-5 w-[3px] rounded-full bg-primary" aria-hidden />
						<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
							{hoveredPoint ? (
								<>
									Total volume ·{" "}
									{format(new Date(hoveredPoint.date), "MMM d, yyyy")}
								</>
							) : (
								<>
									Total volume · {format(dateRange.from, "MMM d")} –{" "}
									{format(dateRange.to, "MMM d, yyyy")}
								</>
							)}
						</p>
					</div>

					<div className="flex flex-wrap items-center gap-1 rounded-md border bg-card p-1">
						{DATE_PRESETS.map((p) => (
							<Button
								key={p.id}
								variant="ghost"
								size="sm"
								onClick={() => applyPreset(p.days)}
								className={`h-8 px-3 text-xs font-medium tabular-nums ${
									activePreset === p.id
										? "bg-accent text-accent-foreground"
										: ""
								}`}
							>
								{p.label}
							</Button>
						))}
						<Popover
							open={isCalendarOpen}
							onOpenChange={handleCalendarOpenChange}
						>
							<PopoverTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className={`h-8 gap-1.5 px-3 text-xs font-medium ${
										activePreset === null
											? "bg-accent text-accent-foreground"
											: ""
									}`}
								>
									<CalendarIcon className="h-3.5 w-3.5" />
									{activePreset === null && dateRange.from && dateRange.to ? (
										<span className="tabular-nums">
											{format(dateRange.from, "MMM d")} –{" "}
											{format(dateRange.to, "MMM d")}
										</span>
									) : (
										<span>Custom</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="end">
								<div className="flex flex-col">
									<Calendar
										mode="range"
										defaultMonth={dateRange.from}
										selected={calendarSelection}
										onSelect={handleDateRangeChange}
										numberOfMonths={isMobile ? 1 : 2}
										disabled={(date) =>
											date > new Date() || date < new Date("2020-01-01")
										}
									/>
									<div className="flex items-center justify-end gap-2 p-3 border-t">
										<Button
											variant="outline"
											size="sm"
											onClick={handleCancelDateRange}
										>
											Cancel
										</Button>
										<Button
											size="sm"
											onClick={handleApplyDateRange}
											disabled={!hasValidRange}
										>
											Apply
										</Button>
									</div>
								</div>
							</PopoverContent>
						</Popover>
					</div>
				</div>

				{/* Headline: giant figure + trend, full width below eyebrow row */}
				<div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 stagger-in">
					<div className="text-4xl font-semibold leading-none tracking-tight tabular-nums sm:text-6xl md:text-7xl">
						{formatCurrency(animatedVolume)}
					</div>
					{totalTrendPct !== null &&
						totalTrendPct !== 0 &&
						hoveredPoint === null && (
							<div className="flex items-baseline gap-2 transition-opacity duration-200">
								<span
									className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold tabular-nums transition-colors duration-300 ease-out ${
										animatedTrend > 0
											? "bg-[var(--trend-positive)]/15 text-[var(--trend-positive)]"
											: "bg-[var(--trend-negative)]/15 text-[var(--trend-negative)]"
									}`}
								>
									{animatedTrend > 0 ? (
										<ArrowUpRight className="h-3.5 w-3.5" />
									) : (
										<ArrowDownRight className="h-3.5 w-3.5" />
									)}
									{Math.abs(animatedTrend).toFixed(1)}%
								</span>
								<span className="text-sm text-muted-foreground">
									vs prior period
								</span>
							</div>
						)}
				</div>

				{overview && (
					<div className="grid gap-6 grid-cols-1 sm:grid-cols-3 stagger-in">
						<div className="rounded-xl border border-(--success-border) bg-(--success-bg) p-5">
							<p className="text-sm font-medium text-(--success-fg) opacity-80">
								Orders today
							</p>
							<p className="mt-2 text-3xl font-bold tracking-tight tabular-nums text-(--success-fg)">
								<AnimatedInt value={overview.ordersLast24h ?? 0} />
							</p>
							<p className="mt-1 text-xs text-(--success-fg) opacity-60">
								<span className="tabular-nums">
									{overview.ordersLast7d ?? 0}
								</span>{" "}
								this week
							</p>
						</div>
						<div className="rounded-xl border border-(--info-border) bg-(--info-bg) p-5">
							<p className="text-sm font-medium text-(--info-fg) opacity-80">
								Orders in 30 days
							</p>
							<p className="mt-2 text-3xl font-bold tracking-tight tabular-nums text-(--info-fg)">
								<AnimatedInt value={overview.ordersLast30d ?? 0} />
							</p>
							<p className="mt-1 text-xs text-(--info-fg) opacity-60">
								<span className="tabular-nums">
									~{((overview.ordersLast30d ?? 0) / 30).toFixed(1)}
								</span>{" "}
								per day average
							</p>
						</div>
						<div className="rounded-xl border border-(--warning-border) bg-(--warning-bg) p-5">
							<p className="text-sm font-medium text-(--warning-fg) opacity-80">
								Active links
							</p>
							<p className="mt-2 text-3xl font-bold tracking-tight tabular-nums text-(--warning-fg)">
								<AnimatedInt value={overview.activePaymentLinks ?? 0} />
							</p>
							<p className="mt-1 text-xs text-(--warning-fg) opacity-60">
								<span className="tabular-nums">
									{overview.uniqueCustomers ?? 0}
								</span>{" "}
								customers reached
							</p>
						</div>
					</div>
				)}

				{totalSeries.length > 0 && (
					<Card className="stagger-in">
						<CardContent>
							<ResponsiveContainer width="100%" height={280}>
								<AreaChart
									data={totalSeries}
									margin={{ top: 8, right: 0, left: -8, bottom: 0 }}
									onMouseMove={(state: {
										activePayload?: Array<{
											payload?: { date: string; volume: number };
										}>;
									}) => {
										const point = state?.activePayload?.[0]?.payload;
										if (point && typeof point.volume === "number") {
											setHoveredPoint({
												date: point.date,
												volume: point.volume,
											});
										}
									}}
									onMouseLeave={() => setHoveredPoint(null)}
								>
									<defs>
										<linearGradient
											id="heroVolumeGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="var(--primary)"
												stopOpacity={0.5}
											/>
											<stop
												offset="50%"
												stopColor="var(--primary)"
												stopOpacity={0.25}
											/>
											<stop
												offset="100%"
												stopColor="var(--primary)"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<XAxis
										dataKey="date"
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
										tickFormatter={(value) => {
											const date = new Date(value);
											return `${date.getMonth() + 1}/${date.getDate()}`;
										}}
									/>
									<YAxis
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
										width={48}
										tickFormatter={(value) =>
											value >= 1000
												? `$${(value / 1000).toFixed(0)}k`
												: `$${value.toFixed(0)}`
										}
									/>
									<Tooltip
										cursor={{
											stroke: "var(--primary)",
											strokeWidth: 1,
											strokeOpacity: 0.5,
										}}
										content={() => null}
									/>
									<Area
										type="monotone"
										dataKey="volume"
										stroke="var(--primary)"
										strokeWidth={2.5}
										fill="url(#heroVolumeGradient)"
										dot={false}
										activeDot={{
											r: 5,
											stroke: "var(--background)",
											strokeWidth: 2,
											fill: "var(--primary)",
										}}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				)}
			</section>

			{!hasAnyData && (
				<div className="flex flex-col items-center gap-4 py-12 text-center">
					<p className="text-sm text-muted-foreground">
						No analytics for this period yet.
					</p>
					<div className="flex flex-wrap items-center justify-center gap-2">
						<Button onClick={() => navigate("/payment-links")}>
							Create your first payment link
						</Button>
						<Button variant="ghost" onClick={() => navigate("/developer")}>
							Read the integration guide
						</Button>
					</div>
				</div>
			)}

			{volumeData?.currencies && volumeData.currencies.length > 1 && (
				<div className="grid gap-6 grid-cols-1 lg:grid-cols-2 stagger-in">
					{volumeData.currencies.map((currencyData, idx) => (
						<Card key={currencyData.currency}>
							<CardHeader className="pb-2">
								<div className="flex items-center justify-between">
									<CardTitle className="text-base font-medium">
										{currencyData.currency} volume
									</CardTitle>
									{currencyData.volumeChangePct !== 0 && (
										<span
											className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
												currencyData.volumeChangePct > 0
													? "bg-[var(--trend-positive)]/15 text-[var(--trend-positive)]"
													: "bg-[var(--trend-negative)]/15 text-[var(--trend-negative)]"
											}`}
										>
											{currencyData.volumeChangePct > 0 ? (
												<ArrowUpRight className="h-3 w-3" />
											) : (
												<ArrowDownRight className="h-3 w-3" />
											)}
											{Math.abs(currencyData.volumeChangePct).toFixed(1)}%
										</span>
									)}
								</div>
								<p className="text-xs text-muted-foreground tabular-nums">
									{formatCurrency(currencyData.totalVolume)}
								</p>
							</CardHeader>
							<CardContent>
								<ResponsiveContainer width="100%" height={200}>
									{(() => {
										const seriesColor =
											getBlockchainColor(currencyData.currency, idx) ||
											CHART_PALETTE[idx % CHART_PALETTE.length];
										return (
											<AreaChart
												data={currencyData.series}
												margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
											>
												<XAxis
													dataKey="date"
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													tickFormatter={(value) => {
														const date = new Date(value);
														return `${date.getMonth() + 1}/${date.getDate()}`;
													}}
												/>
												<YAxis
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													width={48}
													tickFormatter={(value) =>
														value >= 1000
															? `$${(value / 1000).toFixed(0)}k`
															: `$${value.toFixed(0)}`
													}
												/>
												<Tooltip
													content={
														<CustomTooltip
															formatter={(value: number) =>
																`$${value.toFixed(2)}`
															}
														/>
													}
												/>
												<Area
													type="monotone"
													dataKey="volume"
													stroke={seriesColor}
													strokeWidth={2}
													fill={seriesColor}
													fillOpacity={0.08}
													dot={false}
												/>
											</AreaChart>
										);
									})()}
								</ResponsiveContainer>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			<div className="grid gap-6 grid-cols-1 lg:grid-cols-3 stagger-in">
				{orderStatusData?.series && orderStatusData.series.length > 0 && (
					<Card className="lg:col-span-2">
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">
								Order status over time
							</CardTitle>
							<CardDescription className="text-xs">
								Daily order counts by status
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ResponsiveContainer width="100%" height={320}>
								<AreaChart
									data={orderStatusData.series}
									margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
								>
									<defs>
										{(
											[
												["paidGradient", ORDER_STATUS_COLORS.paid],
												[
													"partiallyPaidGradient",
													ORDER_STATUS_COLORS.partiallyPaid,
												],
												["pendingGradient", ORDER_STATUS_COLORS.pending],
												["expiredGradient", ORDER_STATUS_COLORS.expired],
												["cancelledGradient", ORDER_STATUS_COLORS.cancelled],
											] as const
										).map(([id, color]) => (
											<linearGradient
												key={id}
												id={id}
												x1="0"
												y1="0"
												x2="0"
												y2="1"
											>
												<stop offset="0%" stopColor={color} stopOpacity={0.5} />
												<stop
													offset="50%"
													stopColor={color}
													stopOpacity={0.25}
												/>
												<stop offset="100%" stopColor={color} stopOpacity={0} />
											</linearGradient>
										))}
									</defs>
									<XAxis
										dataKey="date"
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
										tickFormatter={(value) => {
											const date = new Date(value);
											return `${date.getMonth() + 1}/${date.getDate()}`;
										}}
									/>
									<YAxis
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
										width={48}
									/>
									<Tooltip
										content={
											<CustomTooltip
												formatter={(value: number, name: string) => {
													const statusNames: Record<string, string> = {
														paid: "Paid",
														partiallyPaid: "Partially Paid",
														pending: "Pending",
														expired: "Expired",
														cancelled: "Cancelled",
													};
													return [value.toString(), statusNames[name] || name];
												}}
											/>
										}
									/>
									<Legend
										wrapperStyle={{
											paddingTop: "20px",
										}}
										iconType="circle"
										formatter={(value: string) => {
											const statusNames: Record<string, string> = {
												paid: "Paid",
												partiallyPaid: "Partially Paid",
												pending: "Pending",
												expired: "Expired",
												cancelled: "Cancelled",
											};
											return (
												<span className="text-xs font-medium text-muted-foreground">
													{statusNames[value] || value}
												</span>
											);
										}}
									/>
									{(
										[
											["paid", ORDER_STATUS_COLORS.paid, "paidGradient"],
											[
												"partiallyPaid",
												ORDER_STATUS_COLORS.partiallyPaid,
												"partiallyPaidGradient",
											],
											[
												"pending",
												ORDER_STATUS_COLORS.pending,
												"pendingGradient",
											],
											[
												"expired",
												ORDER_STATUS_COLORS.expired,
												"expiredGradient",
											],
											[
												"cancelled",
												ORDER_STATUS_COLORS.cancelled,
												"cancelledGradient",
											],
										] as const
									).map(([dataKey, color, gradientId]) => (
										<Area
											key={dataKey}
											type="monotone"
											dataKey={dataKey}
											stroke={color}
											strokeWidth={2.5}
											fill={`url(#${gradientId})`}
											dot={false}
										/>
									))}
								</AreaChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				)}

				{networkData?.networks && networkData.networks.length > 0 && (
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">
								Volume by network
							</CardTitle>
							<CardDescription className="text-xs">
								Distribution across blockchains
							</CardDescription>
						</CardHeader>
						<CardContent>
							{/* One composed bar: each network's segment owns its share
							    of the same 100% width, so the bar reads as a real
							    total. Independent per-row tracks made the bars look
							    like five separate things that didn't add up. */}
							<div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
								{networkData.networks.map((network, index) => {
									const color = getBlockchainColor(network.network, index);
									return (
										<div
											key={network.network}
											className="h-full transition-[width] duration-500 ease-out motion-reduce:transition-none"
											style={{
												width: `${network.percentage}%`,
												backgroundColor: color,
											}}
											title={`${network.network} · ${network.percentage.toFixed(1)}%`}
										/>
									);
								})}
							</div>

							<ul className="mt-5 space-y-3">
								{networkData.networks.map((network, index) => {
									const color = getBlockchainColor(network.network, index);
									return (
										<li
											key={network.network}
											className="flex items-center justify-between gap-3 text-sm"
										>
											<div className="flex min-w-0 items-center gap-2">
												<span
													className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
													style={{ backgroundColor: color }}
												/>
												<span className="truncate font-medium">
													{network.network}
												</span>
												<span className="text-xs text-muted-foreground tabular-nums">
													· {network.orderCount} orders
												</span>
											</div>
											<div className="flex flex-shrink-0 items-center gap-3">
												<span className="font-medium tabular-nums">
													{formatCurrency(network.totalVolume)}
												</span>
												<span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
													{network.percentage.toFixed(1)}%
												</span>
											</div>
										</li>
									);
								})}
							</ul>
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
};

export default DashboardPage;
